/**
 * "Facebook didn't take this post at 9:53 PM: the connection timed out.
 * Nothing was posted. “Game 4 tonight…”" — the Reach calendar's words, plus
 * the post's opening so a Today card says which one.
 */
/** The server's reason for a post whose channel's sign-in ran out (review.js failureReason). */
export const connectionLapsed = (failure: string | null | undefined) =>
  /needs to be renewed/i.test(failure || '');

/** Backlog 8def3fa5: what happened, then what to do, in place of the server's clause. */
export const reconnectWords = (channel: string) =>
  `The ${channel} connection expired, so nothing was posted. Reconnect ${channel}, then try again.`;

export function missedNote(
  channel: string,
  at: Date | null,
  failure: string | null | undefined,
  text: string,
  now = new Date()
) {
  const day = (d: Date) => d.toLocaleDateString([], { weekday: 'short' });
  const whenText = at
    ? at.toDateString() === now.toDateString()
      ? ` at ${clock(at)}`
      : ` on ${day(at)} at ${clock(at)}`
    : '';
  const excerpt = text.length > 80 ? `${text.slice(0, 80).trimEnd()}…` : text;
  if (connectionLapsed(failure)) {
    return `${reconnectWords(channel)}${excerpt ? ` “${excerpt}”` : ''}`;
  }
  return (
    `${channel} didn’t take this post${whenText}${failure ? `: ${failure}` : ''}. Nothing was posted.` +
    (excerpt ? ` “${excerpt}”` : '')
  );
}

const clock = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const monthDay = (d: Date) => d.toLocaleDateString([], { month: 'short', day: 'numeric' });
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

function andList(xs: string[], last = ' and ') {
  return xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')}${last}${xs[xs.length - 1]}`;
}

/**
 * How late posting now would be ("12 min late", "about 2h late", "3 days
 * late"): hours within a day, then calendar days, as the person counts them.
 */
export function lateBy(at: Date, now = new Date()): string {
  const min = (now.getTime() - at.getTime()) / 60000;
  if (min < 60) return `${Math.max(1, Math.round(min))} min late`;
  if (min < 24 * 60) return `about ${Math.round(min / 60)}h late`;
  const days = Math.max(1, Math.round((startOfDay(now) - startOfDay(at)) / 86400000));
  return `${days} day${days === 1 ? '' : 's'} late`;
}

/** The missed post's button: "Try again" within the hour, else how late it would go out. */
export function retryLabel(at: Date | null, now = new Date()): string {
  if (!at || now.getTime() - at.getTime() < 60 * 60000) return 'Try again';
  return `Post now, ${lateBy(at, now)}`;
}

/**
 * Missed posts on the same channel that failed for the same reason, together
 * (Prototype v7: "2 LinkedIn posts missed their time"). In the order given;
 * a post alone stays alone.
 */
export function groupMissed<
  T extends { platform?: string | null; socialProfileId?: string | null; failure?: string | null },
>(posts: T[]): T[][] {
  const groups = new Map<string, T[]>();
  for (const p of posts) {
    const k = `${p.socialProfileId || (p.platform || '').toLowerCase()}|${p.failure || ''}`;
    groups.set(k, [...(groups.get(k) || []), p]);
  }
  return [...groups.values()];
}

// The server's reasons (reach/services/review.js failureReason) are about one
// post; said once for several.
const PLURAL_CAUSE: Record<string, (channel: string) => string> = {
  'it wasn’t sent before its time passed': () => 'they weren’t sent before their time passed',
  'its channel is paused or disconnected, so it wasn’t sent': (c) =>
    `${c} is paused or disconnected, so they weren’t sent`,
  'its connection needs to be renewed in Social → Channels': (c) =>
    `the ${c} connection needs to be renewed in Social → Channels`,
  'its connection had expired and has been renewed since': (c) =>
    `the ${c} connection had expired and has been renewed since`,
  // What an apiserver from before the Social rename (2026-10-28) still says.
  'its connection needs to be renewed in Reach → Channels': (c) =>
    `the ${c} connection needs to be renewed in Social → Channels`,
  'too many posts went to it at once, so it was asked to wait': (c) =>
    `too many posts went to ${c} at once, so it asked them to wait`,
  'it looked the same as an earlier post': () => 'they looked the same as earlier posts',
  'it couldn’t take the image or video': (c) => `${c} couldn’t take their images or videos`,
  'the text is too long for it': (c) => `the text is too long for ${c}`,
  'it returned an error': (c) => `${c} returned an error`,
};

export function sharedCause(failure: string, channel: string): string {
  return PLURAL_CAUSE[failure]?.(channel) || failure;
}

/** "Oct 14", "Oct 14–15", "Sep 30 – Oct 2"; null when every time is today. */
export function dayRange(times: Date[], now = new Date()): string | null {
  const days = [...new Set(times.map(startOfDay))].sort((a, b) => a - b).map((t) => new Date(t));
  if (!days.length || (days.length === 1 && days[0].getTime() === startOfDay(now))) return null;
  const first = days[0];
  const last = days[days.length - 1];
  if (first.getTime() === last.getTime()) return monthDay(first);
  if (first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear())
    return `${monthDay(first)}–${last.getDate()}`;
  return `${monthDay(first)} – ${monthDay(last)}`;
}

/** "at 8:30 AM and 9:00 AM", or "on Oct 14 at 8:30 PM and 8:45 PM, and on Oct 15 at 8:30 PM". */
export function missedTimes(times: Date[], now = new Date()): string {
  const byDay = new Map<number, Date[]>();
  for (const t of [...times].sort((a, b) => a.getTime() - b.getTime())) {
    byDay.set(startOfDay(t), [...(byDay.get(startOfDay(t)) || []), t]);
  }
  const today = startOfDay(now);
  if (byDay.size === 1 && byDay.has(today)) return `at ${andList(byDay.get(today)!.map(clock))}`;
  const parts = [...byDay].map(
    ([day, ts]) =>
      `${day === today ? 'today' : `on ${monthDay(ts[0])}`} at ${andList(ts.map(clock))}`
  );
  return andList(parts, ', and ');
}

/** A Today card for several missed posts on one channel, for one reason. */
export function missedGroupWords(
  channel: string,
  times: Date[],
  failure: string | null | undefined,
  now = new Date()
) {
  const n = times.length;
  const range = dayRange(times, now);
  const newest = times.reduce((a, b) => (b > a ? b : a), times[0]);
  const went = `nothing went out ${missedTimes(times, now)}`;
  return {
    preview: `${n} ${channel} posts`,
    title: `${n} ${channel} posts missed their time${range ? `, ${range}` : ''}`,
    corner: range || 'Today',
    note: connectionLapsed(failure)
      ? `Same cause for ${n === 2 ? 'both' : `all ${n}`}: ${reconnectWords(channel)}`
      : failure
        ? `Same cause for ${n === 2 ? 'both' : `all ${n}`}: ${sharedCause(failure, channel)}, so ${went}.`
        : `${went[0].toUpperCase()}${went.slice(1)}.`,
    cta: newest ? `Post now, ${lateBy(newest, now)}` : 'Post now',
    skip: 'Skip all',
    posted: `Posting ${n} now. They go out within a few minutes.`,
    skipped: `Skipped ${n}. They won’t be posted.`,
  };
}

// "Oct. 20", "October 20, 2026", "Tuesday, Oct. 20": a day a post talks about.
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const DAY_RE =
  /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+(\d{1,2})(?:st|nd|rd|th)?\b(?:,?\s+(\d{4}))?/gi;
// "2 p.m.", "11:30 am", "3:41 PM": a time near that day.
const TIME_RE = /\b(\d{1,2})(?::(\d{2}))?\s*([ap])\.?\s?m\.?(?![a-z])/i;
const NEAR = 60;

/**
 * The latest day (and time, when one is written next to it) a post's text
 * mentions that has already passed: { at, label: "Oct 20, 2 PM" }, or null.
 * A day without a year is the year the post was meant for (or the next, when
 * it falls well before then: "Jan. 5" in a December post). Days are read in
 * the browser's time zone; a written zone ("EDT") is not converted.
 */
export function passedMention(
  text: string,
  meantFor: Date | null,
  now = new Date()
): { at: Date; label: string } | null {
  const base = meantFor || now;
  let best: { at: Date; label: string } | null = null;
  for (const m of text.matchAll(DAY_RE)) {
    const month = MONTHS.indexOf(m[1].slice(0, 3).toLowerCase());
    const day = Number(m[2]);
    if (month < 0 || day < 1 || day > 31) continue;
    // A day in an earlier year ("snapped on October 7, 2014") is history, not
    // an event the post is timed for (UX review round 20): never flag it.
    if (m[3] && Number(m[3]) < base.getFullYear()) continue;
    let year = m[3] ? Number(m[3]) : base.getFullYear();
    if (!m[3] && new Date(year, month, day).getTime() < base.getTime() - 60 * 86400000) year += 1;
    const around = text.slice(Math.max(0, m.index! - NEAR), m.index! + m[0].length + NEAR);
    const t = TIME_RE.exec(around);
    let hour = 23;
    let minute = 59;
    if (t) {
      hour = (Number(t[1]) % 12) + (t[3].toLowerCase() === 'p' ? 12 : 0);
      minute = t[2] ? Number(t[2]) : 0;
    }
    const at = new Date(year, month, day, hour, minute);
    if (at.getTime() >= now.getTime()) continue;
    const label =
      `${at.toLocaleDateString([], { month: 'short', day: 'numeric' })}` +
      (t
        ? `, ${at.toLocaleTimeString([], { hour: 'numeric', minute: t[2] ? '2-digit' : undefined })}`
        : '');
    if (!best || at > best.at) best = { at, label };
  }
  return best;
}

/** How much later than its time a post that never went out is: hours. */
const hoursLate = (at: Date, now: Date) => (now.getTime() - at.getTime()) / 3600000;

/**
 * Why a missed post shouldn't just go out now (Prototype v16, UX review round
 * 9): its text mentions a day or time that has passed ("Mentions Oct 20, 2 PM,
 * already passed"), or it's more than 48 hours late ("5 days late, old news by
 * now"). Null when posting late is still fine ("Post now, about 2h late").
 */
export function staleReason(text: string, at: Date | null, now = new Date()): string | null {
  const mention = passedMention(text, at, now);
  if (mention) return `Mentions ${mention.label}, already passed`;
  if (at && hoursLate(at, now) > 48) return `${lateBy(at, now)}, old news by now`;
  return null;
}

/**
 * Reach → Calendar's strip (UX review round 11): the posts that didn't go out
 * and still wait for a decision — the list Today and Channels show — that sit
 * outside the week on screen. "3 Facebook posts didn't go out, Oct 14–15";
 * Review opens the first channel's list. Null when none does. Inside the week
 * they show on their day, as "didn't post", as before.
 *
 * On one channel whose connection expired they can't be posted, so the words
 * say why and `reconnect` is that channel (backlog 91a63097). Across channels
 * the words stay general: the cause may not be shared.
 */
export function undecidedOutsideWeek<
  T,
  C extends { id: string; platform: string; missedPosts?: { posts?: T[] | null } | null },
>(
  channels: C[],
  weekStart: Date,
  weekEnd: Date,
  timeOf: (p: T) => Date | null,
  nameOf: (platform: string) => string,
  now = new Date(),
  expired: (c: C) => boolean = () => false
): { count: number; title: string; sub: string; channelId: string; reconnect?: C } | null {
  const outside = channels
    .map((c) => ({
      c,
      posts: (c.missedPosts?.posts || []).filter((p) => {
        const t = timeOf(p);
        return !t || t < weekStart || t >= weekEnd;
      }),
    }))
    .filter((x) => x.posts.length);
  if (!outside.length) return null;
  const count = outside.reduce((n, x) => n + x.posts.length, 0);
  const names = [...new Set(outside.map((x) => nameOf(x.c.platform)))];
  const range = dayRange(
    outside.flatMap((x) => x.posts.map(timeOf)).filter((t): t is Date => Boolean(t)),
    now
  );
  const one = count === 1;
  const title = `${count} ${names.length === 1 ? `${names[0]} ` : ''}${one ? 'post' : 'posts'} didn’t go out`;
  if (outside.length === 1 && expired(outside[0].c)) {
    const them = one ? 'it' : 'them';
    return {
      count,
      title: `${title}${range ? `, ${range},` : ''} because the connection expired.`,
      sub: `Reconnect to post ${them}, or skip ${them}.`,
      channelId: outside[0].c.id,
      reconnect: outside[0].c,
    };
  }
  return {
    count,
    title: `${title}${range ? `, ${range}` : ''}`,
    sub: `${one ? 'It’s' : 'They’re'} outside this week. Post or skip ${
      one ? 'it' : 'them'
    } so nothing goes out late by surprise.`,
    channelId: outside[0].c.id,
  };
}
